import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { DeviceMode, DEVICE_VIEWPORTS } from "./editor-preview-viewports";

type FrameMessage =
  | { type: "EDITOR_READY" }
  | { type: "EDITOR_INIT"; payload: { page: string; content: unknown } }
  | { type: "EDITOR_SYNC"; payload: { page: string; content: unknown } }
  | { type: "EDITOR_UPDATE"; payload: { page: string; content: unknown } };

interface EditorPreviewFrameProps {
  deviceMode: DeviceMode;
  page: string;
  pageId?: string | null;
  content: unknown;
  onContentChange: (next: unknown) => void;
  className?: string;
}

export function EditorPreviewFrame({
  deviceMode,
  page,
  pageId,
  content,
  onContentChange,
  className,
}: EditorPreviewFrameProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const lastSentRef = useRef<string>("");
  const contentRef = useRef<unknown>(content);
  const [iframeLoadCount, setIframeLoadCount] = useState(0);

  const debugEnabled = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("debug") === "1";
    } catch {
      return false;
    }
  }, []);

  // Keep latest content in a ref so the postMessage handshake never uses stale closures
  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  const viewport = DEVICE_VIEWPORTS[deviceMode];

  // Observe container size
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) setContainerSize({ width: rect.width, height: rect.height });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Calculate scale to fit viewport into container
  const scale = useMemo(() => {
    if (!containerSize.width || !containerSize.height) return 1;

    // For desktop, fit to width only and allow vertical scroll
    // For mobile/tablet, fit both dimensions
    const scaleX = containerSize.width / viewport.width;

    if (deviceMode === "desktop") {
      // Desktop: fit width, allow scroll
      return Math.min(1, scaleX);
    } else {
      // Mobile/tablet: fit both dimensions with small padding for device frame
      const padding = 60;
      const scaleY = (containerSize.height - padding) / viewport.height;
      return Math.min(1, scaleX, scaleY);
    }
  }, [containerSize.width, containerSize.height, viewport.width, viewport.height, deviceMode]);

  // Memoize src to prevent iframe remounting on every render
  // Use a stable key for the iframe to avoid unnecessary reloads
  const src = useMemo(() => {
    const params: Record<string, string> = { page };
    if (pageId) params.pageId = pageId;
    const sp = new URLSearchParams(params);
    return `/admin/editor-frame?${sp.toString()}`;
  }, [page, pageId]);

  // Stable iframe key based only on page/pageId to prevent remounting during content changes
  const iframeKey = useMemo(() => `iframe-${page}-${pageId || 'builtin'}`, [page, pageId]);

  const postToIframe = (msg: FrameMessage) => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(msg, window.location.origin);
  };

  // Listen for iframe updates
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as FrameMessage | undefined;
      if (!data || typeof data !== "object") return;

      if (data.type === "EDITOR_READY") {
        // Handshake: iframe is ready, send the latest snapshot
        postToIframe({ type: "EDITOR_INIT", payload: { page, content: contentRef.current } });
        return;
      }

      if (data.type === "EDITOR_UPDATE") {
        if (data.payload?.page !== page) return;
        onContentChange(data.payload.content);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // IMPORTANT: do not depend on `content` here, otherwise we re-register the listener
    // and can miss the iframe's single READY message.
  }, [page, onContentChange]);

  // Sync content to iframe whenever it changes (and differs)
  useEffect(() => {
    try {
      const serialized = JSON.stringify(content);
      if (serialized === lastSentRef.current) return;
      lastSentRef.current = serialized;
    } catch {
      // ignore
    }

    postToIframe({ type: "EDITOR_SYNC", payload: { page, content } });
  }, [page, content]);

  const isFramed = deviceMode !== "desktop";

  // If the iframe loads without us catching EDITOR_READY (race conditions happen),
  // proactively send INIT/SYNC on load as a fallback.
  const handleIframeLoad = () => {
    setIframeLoadCount((c) => c + 1);
    postToIframe({ type: "EDITOR_INIT", payload: { page, content: contentRef.current } });
    postToIframe({ type: "EDITOR_SYNC", payload: { page, content: contentRef.current } });
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex-1 flex justify-center relative overflow-auto",
        deviceMode === "desktop" ? "bg-transparent" : "bg-muted/30 items-center pt-4",
        className
      )}
    >
      {debugEnabled && (
        <div className="pointer-events-none absolute bottom-3 left-3 z-[100] max-w-[520px] rounded-lg border border-border bg-card/90 backdrop-blur px-3 py-2 text-xs text-foreground">
          <div className="font-medium">Preview Debug</div>
          <div className="mt-1 text-muted-foreground">iframe src: {src}</div>
          <div className="mt-1 text-muted-foreground">iframe loads: {iframeLoadCount}</div>
          <div className="mt-1 text-muted-foreground">page: {page}</div>
          <div className="mt-1 text-muted-foreground">pageId: {String(pageId ?? "null")}</div>
        </div>
      )}

      {/* Wrapper for full viewport size before scaling */}
      <div
        className={cn(
          "transition-transform duration-200 ease-out",
          deviceMode === "desktop" ? "origin-top" : "origin-top-center"
        )}
        style={{
          transform: `scale(${scale})`,
          width: `${viewport.width}px`,
          height: deviceMode === "desktop" ? "auto" : `${viewport.height}px`,
          minHeight: deviceMode === "desktop" ? `${viewport.height}px` : undefined,
        }}
      >
        {isFramed && (
          <div
            className={cn(
              "absolute pointer-events-none z-10",
              deviceMode === "mobile"
                ? "-inset-4 rounded-[52px] border-[12px] border-neutral-900 dark:border-neutral-700"
                : "-inset-3 rounded-[36px] border-[10px] border-neutral-800 dark:border-neutral-600"
            )}
            style={{ boxShadow: "0 25px 60px -12px rgba(0, 0, 0, 0.5)" }}
          >
            {deviceMode === "mobile" && (
              <>
                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[100px] h-[30px] bg-neutral-900 dark:bg-black rounded-full" />
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[120px] h-[5px] bg-neutral-600 rounded-full" />
              </>
            )}
          </div>
        )}

        <iframe
          key={iframeKey}
          ref={iframeRef}
          title="Editor Preview"
          src={src}
          onLoad={handleIframeLoad}
          style={{
            width: `${viewport.width}px`,
            height: `${viewport.height}px`,
            border: "none",
          }}
          className={cn(
            "bg-background",
            // Use fixed preview-frame radii. Tenant themes can set --radius to 9999px
            // for pill buttons; using Tailwind's var-backed rounded-lg here turns the
            // desktop iframe into a giant oval and clips the website preview.
            deviceMode === "desktop" && "rounded-[12px] shadow-2xl border border-border",
            deviceMode === "tablet" && "rounded-[26px]",
            deviceMode === "mobile" && "rounded-[40px]"
          )}
        />
      </div>
    </div>
  );
}
